import { useEffect, useState } from 'react'
import { COURSE_EXCEPTION_TYPE } from '../../domain/domainCodes'
import { Pencil, Plus, Search, Trash2, UserCog } from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import DatePicker from '../../components/DatePicker'
import Drawer from '../../components/Drawer'
import { FormBody, FormCols, FormWell } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import SegmentedControl from '../../components/SegmentedControl'
import TextField from '../../components/TextField'
import { ApiError } from '../../lib/api'
import { listUsers } from '../users/usersService'
import {
  listExceptionUsers,
  removeUsersExceptions,
  setUsersExceptions,
} from './programsService'
import { strings as S } from './strings'
import { isoDate } from '../../lib/spreadsheet'
import './CourseExceptionsDrawer.css'
import { displayDate } from '../../lib/dates'

const X = S.exceptions

/** `CourseExceptionType` — the numbers the column stores. */
const TRIES = COURSE_EXCEPTION_TYPE.EXAM_TRIES
const UNTIL = COURSE_EXCEPTION_TYPE.ATTEND_UNTIL
const BOTH = COURSE_EXCEPTION_TYPE.BOTH

const hasTries = (type) => type === TRIES || type === BOTH
const hasUntil = (type) => type === UNTIL || type === BOTH

/** The date arrives as an ISO instant and goes back as `YYYY-MM-DD`. */
const asDate = (value) => (value ? String(value).slice(0, 10) : '')

const EMPTY = { users: [], type: BOTH, examTries: '', canAttendUntil: '' }

/**
 * The user picker for a NEW exception.
 *
 * `/Users/Filter` is the only list of users this app has, and it is guarded by
 * `UsersManagers` while this screen is guarded by `ContentManagers` — an administrator who
 * only manages content gets a 403 here. That is not a failure to report: the command
 * itself takes plain usernames, so the picker simply becomes a field to type them into.
 */
function UserPicker({ chosen, onChange }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [denied, setDenied] = useState(false)
  const [manual, setManual] = useState('')

  const search = async () => {
    const term = query.trim()
    if (!term) return
    setSearching(true)
    try {
      const { rows } = await listUsers({ search: term, pageSize: 8 })
      setResults(rows)
    } catch (error) {
      if (!(error instanceof ApiError)) throw error
      // 403 or anything else from the list: fall through to typing usernames.
      setDenied(true)
    }
    setSearching(false)
  }

  // Typed usernames are separated by whatever came to hand — comma, newline or space.
  const commitManual = (value) => {
    setManual(value)
    onChange(value.split(/[\s,;]+/).map((name) => name.trim()).filter(Boolean))
  }

  if (denied) {
    return (
      <FormRow label={X.usernames} hint={X.usernamesHint}>
        {(id) => (
          <TextField id={id} dir="ltr" value={manual} onChange={commitManual} />
        )}
      </FormRow>
    )
  }

  const toggle = (name) => (on) =>
    onChange(on ? [...chosen, name] : chosen.filter((value) => value !== name))

  return (
    <div className="cexc__picker">
      <FormRow label={X.findUser} hint={X.findUserHint}>
        {(id) => (
          <div className="cexc__search">
            <TextField
              id={id}
              value={query}
              onChange={setQuery}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                search()
              }}
            />
            <Button variant="secondary" disabled={searching} onClick={search}>
              <Search size={16} strokeWidth={2} />
              {searching ? X.searching : X.search}
            </Button>
          </div>
        )}
      </FormRow>

      {results.length > 0 && (
        <ul className="cexc__results">
          {results.map((user) => (
            <li key={user.userName}>
              <Checkbox
                id={`cexc-${user.userName}`}
                checked={chosen.includes(user.userName)}
                onChange={toggle(user.userName)}
              >
                <span className="cexc__result">
                  <bdi>{user.name || user.userName}</bdi>
                  <span className="cexc__result-id" dir="ltr">
                    {user.userName}
                  </span>
                </span>
              </Checkbox>
            </li>
          ))}
        </ul>
      )}

      {chosen.length > 0 && <p className="cexc__chosen">{X.chosen(chosen.length)}</p>}
    </div>
  )
}

/**
 * استثناءات المتدربين — extra exam attempts, a later deadline, or both, for named users on
 * one course.
 *
 * Three endpoints have been in the service since the slice was wired with nothing in front
 * of them: list, set, remove. This is the screen for them.
 *
 * `GetExceptionsUsers` lists ONLY users who already have an exception, so adding one needs
 * a picker of its own — see `UserPicker`. `SetUsersFromExceptions` is an upsert keyed on
 * (program, course, user), which makes editing a row the same call as creating one.
 */
export default function CourseExceptionsDrawer({ open, programId, course, onClose, onOutcome }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [query, setQuery] = useState('')
  /* The order the handler applies on its own — by name — so the arrow agrees with the rows. */
  const [sort, setSort] = useState({ key: 'user', dir: 'asc' })
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [error, setError] = useState('')

  const courseId = course?.id

  const load = async (next = {}) => {
    if (!courseId) return
    setLoading(true)
    const result = await listExceptionUsers({
      programId,
      courseId,
      page: next.page ?? page,
      pageSize: next.pageSize ?? pageSize,
      search: next.search ?? query,
      sort: next.sort ?? sort,
    })
    setRows(result.rows)
    setTotal(result.total)
    setLoading(false)
  }

  useEffect(() => {
    if (!open) return
    setForm(null)
    setError('')
    setPage(1)
    setQuery('')
    load({ page: 1, search: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, courseId])

  const set = (key) => (value) => {
    setForm((previous) => ({ ...previous, [key]: value }))
    setError('')
  }

  const submit = async () => {
    if (form.users.length === 0) {
      setError(X.usersRequired)
      return
    }
    if (hasTries(form.type) && !String(form.examTries).trim()) {
      setError(X.triesRequired)
      return
    }
    const tries = Number(form.examTries)
    if (hasTries(form.type) && !(Number.isInteger(tries) && tries >= 1 && tries <= 100)) {
      setError(X.triesRange)
      return
    }
    if (hasUntil(form.type) && !form.canAttendUntil) {
      setError(X.untilRequired)
      return
    }
    /* An extension that ended yesterday extends nothing. */
    if (hasUntil(form.type) && form.canAttendUntil < isoDate(new Date())) {
      setError(X.untilPast)
      return
    }

    setSaving(true)
    const outcome = await setUsersExceptions({
      users: form.users,
      programId,
      courseId,
      type: form.type,
      // A field the chosen type does not cover is cleared rather than left behind — the
      // row keeps both columns, and a stale deadline would still be honoured.
      canAttendUntil: hasUntil(form.type) ? form.canAttendUntil : null,
      examTries: hasTries(form.type) ? Number(form.examTries) : null,
    })
    setSaving(false)

    if (!outcome.ok) {
      setError(outcome.message || S.actionFailed)
      return
    }
    onOutcome?.(outcome.message)
    setForm(null)
    load()
  }

  /* `render` is what DataTable calls. The list is server-paged, so a sortable column is one
     `GetExceptionsUsersHandler` can order by; the row number and the actions are not. */
  const columns = [
    {
      key: 'rowNumber',
      header: X.columns.index,
      width: 56,
      sortable: false,
      /* The DTO is PascalCase on the wire — this list is not passed through a row mapper,
         because nothing but this drawer reads it. */
      render: (row) => row.RowNumber,
    },
    {
      key: 'user',
      header: X.columns.user,
      render: (row) => (
        <span className="cexc__user">
          <bdi className="cexc__user-name">{row.FullName || row.UserName}</bdi>
          <span className="cexc__user-id" dir="ltr">
            {row.UserName}
          </span>
        </span>
      ),
    },
    {
      key: 'department',
      header: X.columns.department,
      render: (row) => row.Department || '—',
    },
    {
      key: 'tries',
      header: X.columns.tries,
      render: (row) => (hasTries(row.ExceptionType) ? (row.NumberOfExamTries ?? '—') : '—'),
    },
    {
      key: 'until',
      header: X.columns.until,
      render: (row) => (hasUntil(row.ExceptionType) ? displayDate(asDate(row.CanAttendUntil)) || '—' : '—'),
    },
    {
      key: 'actions',
      header: '',
      width: 88,
      sortable: false,
      render: (row) => (
        <span className="cexc__row-actions">
          <IconButton
            icon={Pencil}
            label={X.edit}
            onClick={() =>
              setForm({
                users: [row.UserName],
                locked: row.FullName || row.UserName,
                type: row.ExceptionType || BOTH,
                examTries: row.NumberOfExamTries ?? '',
                canAttendUntil: asDate(row.CanAttendUntil),
              })
            }
          />
          <IconButton
            icon={Trash2}
            label={X.remove}
            tone="danger"
            onClick={() => setRemoving(row)}
          />
        </span>
      ),
    },
  ]

  const types = [
    { id: TRIES, label: X.types.tries },
    { id: UNTIL, label: X.types.until },
    { id: BOTH, label: X.types.both },
  ]

  return (
    <Drawer
      dialog
      neutral
      open={open}
      size="xl"
      title={X.title}
      icon={UserCog}
      subtitle={course?.name}
      hint={X.hint}
      onClose={onClose}
    >
      {form ? (
        <FormWell>
          <FormBody>
            {form.locked ? (
              <FormRow label={X.columns.user}>{() => <bdi>{form.locked}</bdi>}</FormRow>
            ) : (
              <UserPicker chosen={form.users} onChange={set('users')} />
            )}

            <FormRow label={X.type}>
              {() => (
                <SegmentedControl
                  items={types}
                  value={form.type}
                  onChange={set('type')}
                  ariaLabel={X.type}
                />
              )}
            </FormRow>

            <FormCols>
              {hasTries(form.type) && (
                <FormRow label={X.columns.tries} hint={X.triesHint}>
                  {(id) => (
                    <TextField
                      id={id}
                      type="number"
                      min={1}
                      value={form.examTries}
                      onChange={set('examTries')}
                    />
                  )}
                </FormRow>
              )}
              {hasUntil(form.type) && (
                <FormRow label={X.columns.until} hint={X.untilHint}>
                  {(id) => (
                    <DatePicker
                      id={id}
                      clearable
                      min={isoDate(new Date())}
                      value={form.canAttendUntil}
                      onChange={set('canAttendUntil')}
                    />
                  )}
                </FormRow>
              )}
            </FormCols>

            {error && <p className="cexc__error">{error}</p>}

            <div className="cexc__actions">
              <Button variant="ghost" onClick={() => setForm(null)}>
                {S.close}
              </Button>
              <Button disabled={saving} onClick={submit}>
                {saving ? X.saving : X.save}
              </Button>
            </div>
          </FormBody>
        </FormWell>
      ) : (
        <DataTable
          /* A list inside a sheet is opened to answer one question; it starts at the top. */
          stateKey={false}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.UserName}
          loading={loading}
          emptyMessage={X.empty}
          toolbar={
            <Button onClick={() => setForm(EMPTY)}>
              <Plus size={16} strokeWidth={2} />
              {X.add}
            </Button>
          }
          query={query}
          server={{
            total,
            page,
            pageSize,
            sort,
            onChange: (next) => {
              setPage(next.page)
              setPageSize(next.pageSize)
              setQuery(next.query)
              setSort(next.sort)
              load({ ...next, search: next.query })
            },
          }}
        />
      )}

      <ConfirmDialog
        open={!!removing}
        message={removing ? X.removeConfirm(removing.FullName || removing.UserName) : ''}
        confirmLabel={X.remove}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await removeUsersExceptions({
            users: [removing.UserName],
            programId,
            courseId,
          })
          setRemoving(null)
          onOutcome?.(outcome.message, !outcome.ok)
          if (outcome.ok) load()
        }}
        onCancel={() => setRemoving(null)}
      />
    </Drawer>
  )
}
